import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';
import IIsNotCalibratedReason = ZoneIQCalibration.IIsNotCalibratedReason;
import { environment } from '../../../environments/environment';
import { tap, map, catchError } from 'rxjs/operators';
import { IsNotCalibratedReasonModel } from 'src/app/models/common-models/is-not-calibrated-reason.model';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { BaseAPIService } from './base-api.service';
import { IsNotCalibratedReason } from 'src/app/interfaces/is-not-calibrated-reason.interface';

const API = environment.ZONEIQ_CALIBRATION + '/v1/is-not-calibrated-reasons';

@Injectable({
    providedIn: 'root'
})
export class IsNotCalibratedReasonService extends BaseAPIService {
    isNotCalibratedReasons: BehaviorSubject<IIsNotCalibratedReason[]> = new BehaviorSubject<IIsNotCalibratedReason[]>([]);

    constructor(private readonly http: HttpClient) {
        super();
    }

    getAll() {
        return this.http.get<Response>(API).pipe(
            tap(response => {
                const reasons = this.getData<IIsNotCalibratedReason[]>(response, [])
                    .map(reasonDTO => IsNotCalibratedReasonModel.factory(reasonDTO))
                    .filter(reason => reason);
                this.isNotCalibratedReasons.next(reasons);
            })
        );
    }

    deleteIsNotCalibratedReason(reasonId: number): Observable<IsNotCalibratedReason> {
        if (!reasonId) {
            return;
        }
        const url = `${API}/${reasonId}`;
        return this.http.delete<Response>(url).pipe(
            map(response => {
                const reasonDTO = this.getData<IIsNotCalibratedReason>(response);
                return reasonDTO ? IsNotCalibratedReasonModel.factory(reasonDTO) : null;
            }),
            tap(reason => {
                this.isNotCalibratedReasons.next(this.isNotCalibratedReasons.value.filter(
                    cachedReason => cachedReason.Id !== reason.Id
                ));
                return reason;
            })
        );
    }

    updatedIsNotCalibratedReason(reason: IIsNotCalibratedReason): Observable<IsNotCalibratedReason> {
        if (!_.get(reason, 'Id')) {
            return of();
        }
        const url = `${API}/${reason.Id}`;
        return this.http.post<Response>(url, _.pick(reason, ['Reason', 'IsDefault', 'Order'])).pipe(
            map(response => {
                const reasonDTO = this.getData<IIsNotCalibratedReason>(response);
                return reasonDTO ? IsNotCalibratedReasonModel.factory(reasonDTO) : null;
            }),
            tap(reason => {
                if (!reason) {
                    return;
                }
                const updatedReasons = [...this.isNotCalibratedReasons.value];
                const index = updatedReasons.findIndex(existingReason => existingReason.Id == reason.Id);
                if (index >= 0) {
                    updatedReasons[index] = reason;
                } else {
                    updatedReasons.push(reason);
                }
                this.isNotCalibratedReasons.next(updatedReasons);
                return reason;
            })
        );
    }

    createIsNotCalibratedReason(reason: IIsNotCalibratedReason): Observable<IsNotCalibratedReason> {
        if (!reason) {
            return of();
        }
        const url = `${API}`;
        return this.http.post<Response>(url, _.pick(reason, ['Reason', 'IsDefault', 'Order'])).pipe(
            map(response => {
                const reasonDTO = this.getData<IIsNotCalibratedReason>(response);
                return reasonDTO ? IsNotCalibratedReasonModel.factory(reasonDTO) : null;
            }),
            tap(reason => {
                if (!reason) {
                    return;
                }

                this.isNotCalibratedReasons.next([...this.isNotCalibratedReasons.value, reason]);
                return reason;
            })
        );
    }

    reorderIsNotCalibratedReasons(reasons: IIsNotCalibratedReason[]): Observable<IIsNotCalibratedReason[]> {
        if (!Array.isArray(reasons) || !reasons.length) {
            return of(this.isNotCalibratedReasons.value);
        }

        const reasonIds = reasons
            .map(reason => Number(reason?.Id))
            .filter(reasonId => Number.isInteger(reasonId));

        if (!reasonIds.length) {
            return of(this.isNotCalibratedReasons.value);
        }

        const url = `${API}/reorder`;
        const previousReasons = [...this.isNotCalibratedReasons.value];
        const updatedReasons = reasons.map((reason, index) => ({
            ...reason,
            Order: index + 1
        }));

        // Keep dropped order in the UI while the reorder request is in progress.
        this.isNotCalibratedReasons.next(updatedReasons);

        return this.http.post<Response>(url, { reasonIds }).pipe(
            map(() => updatedReasons),
            catchError(error => {
                this.isNotCalibratedReasons.next(previousReasons);
                return throwError(() => error);
            })
        );
    }
}
